import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";

const Stepper = ({ activeStep, steps, isWide = false }) => {
  const translate = useLanguageTranslate();
  const { i18n } = useTranslation();
  const isRTL = i18n.resolvedLanguage === "ar";
  const stepIndex = steps.findIndex((step) => step?.key == activeStep?.key);

  // Mobile detection
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 767);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Conditional width based on isWide prop
  const containerWidth = isWide
    ? "w-[40%] mx-auto my-0 inset-x-0 max767:hidden ipad:w-[65%] ipad:ml-[17.5%]"
    : "w-[30%] mx-auto my-0 inset-x-0 max767:hidden ipad:w-[55%] ipad:ml-[25%]";

  return (
    <>
      {/* Desktop Stepper - Conditional rendering to avoid CSS specificity issues */}
      {!isMobile && (
        <div
          className={`z-[99999] absolute ${containerWidth}`}
          style={{
            display: "grid",
            gridTemplateColumns:
              steps.length === 5 && isRTL
                ? "1fr 1fr 1fr 1.5fr 1.5fr"
                : `repeat(${steps.length}, 1fr)`,
            alignItems: "center",
            gap: "0",
          }}
        >
          {steps.map((step, index) => {
            return (
              <div
                key={index}
                className="flex flex-col items-center relative w-full"
                style={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                {/* Progress line container */}
                <div className="flex items-center w-full relative h-1.5">
                  {/* Left spacer for first step */}
                  {index === 0 && <div className="flex-1"></div>}

                  {/* Left half line - only for middle and end steps */}
                  {index > 0 && (
                    <div
                      className={`h-[0.1rem] flex-1 ${
                        index <= stepIndex ? "bg-[#55C6EF]" : "bg-[#DDE2E4]"
                      }`}
                      style={{
                        transition: "background-color 0.5s ease-in-out",
                      }}
                    ></div>
                  )}

                  {/* Right half line - only for start and middle steps */}
                  {index < steps.length - 1 && (
                    <div
                      className={`h-[0.1rem] flex-1 ${
                        index < stepIndex ? "bg-[#55C6EF]" : "bg-[#DDE2E4]"
                      }`}
                      style={{
                        transition: "background-color 0.5s ease-in-out",
                      }}
                    ></div>
                  )}

                  {/* Right spacer for last step */}
                  {index === steps.length - 1 && <div className="flex-1"></div>}

                  {/* Active dot - only show for current step */}
                  {index === stepIndex && (
                    <div
                      className="w-1.5 h-1.5 rounded-full bg-[#55C6EF] absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2"
                      style={{
                        transition: "background-color 0.5s ease-in-out",
                      }}
                    ></div>
                  )}
                </div>

                {/* Label - perfectly centered under dot */}
                <div
                  className={`mt-2 text-sm not-italic font-normal leading-6 text-center ${
                    index == stepIndex
                      ? "text-[#1A2024] font-semibold"
                      : "text-[#1A2024]"
                  }`}
                  style={{
                    whiteSpace: "nowrap",
                    width: "max-content",
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {translate(step.title_en, step.title_ar)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Mobile Stepper - Conditional rendering */}
      {isMobile && (
        <div className="fixed top-14 left-0 right-0 z-40 bg-white py-2 border-none">
          <div className="flex items-center px-0">
            <div className="flex items-center w-full">
              {steps.map((step, index) => (
                <div key={index} className="flex items-center flex-1">
                  {/* Progress Bar */}
                  <div
                    className={`h-1 rounded-full transition-all duration-300 flex-1 ${
                      index <= stepIndex ? "bg-[#55C6EF]" : "bg-[#DDE2E4]"
                    }`}
                  ></div>
                  {/* Separator */}
                  {index < steps.length - 1 && <div className="w-1"></div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Stepper;
